import type { EstimatedFulfillmentCost } from "@/types/productOpportunity";

interface Props {
  estimatedMarketValue: number;
  estimatedCostBasis: number;
  expectedSalePrice: number;
  expectedProfit: number;
  /** Profit / salePrice ratio in the 0..1 range (NOT 0..100). */
  expectedMarginPct: number;
  fulfillment: EstimatedFulfillmentCost;
  /** Optional wrapper className for callers that need a different border/padding. */
  className?: string;
}

/**
 * Standard pricing-summary block shared by `/products/studio` opportunity
 * cards and the `/products/deck-import` preview. Single source of truth for
 * how Market / Cost / Expected sale / Shipping / Supplies / Profit / Margin
 * line up.
 */
export function ProductPricingSummary({
  estimatedMarketValue,
  estimatedCostBasis,
  expectedSalePrice,
  expectedProfit,
  expectedMarginPct,
  fulfillment,
  className,
}: Props) {
  const isProfitable = expectedProfit >= 0;
  return (
    <div
      className={
        className ?? "space-y-1 rounded-md border border-border bg-card p-2.5"
      }
    >
      <Row label="Market value" value={`$${estimatedMarketValue.toFixed(2)}`} />
      <Row label="Cost basis" value={`$${estimatedCostBasis.toFixed(2)}`} />
      <Row
        label="Expected sale"
        value={`$${expectedSalePrice.toFixed(2)}`}
        bold
      />
      <Row
        label={`Shipping (${fulfillment.recommendedShippingMethod})`}
        value={`$${fulfillment.postageEstimate.toFixed(2)}`}
      />
      <Row label="Supplies" value={`$${fulfillment.suppliesCost.toFixed(2)}`} />
      <div className="my-1 h-px bg-border" />
      <Row
        label="Expected profit"
        value={`$${expectedProfit.toFixed(2)}`}
        valueColor={isProfitable ? "text-emerald-600" : "text-destructive"}
        bold
      />
      <Row
        label="Margin"
        value={`${(expectedMarginPct * 100).toFixed(0)}%`}
        valueColor={isProfitable ? undefined : "text-destructive"}
      />
    </div>
  );
}

function Row({
  label,
  value,
  bold,
  valueColor,
}: {
  label: string;
  value: string;
  bold?: boolean;
  valueColor?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={`${bold ? "font-bold" : "font-medium"} ${valueColor ?? "text-foreground"}`}
      >
        {value}
      </span>
    </div>
  );
}
